import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Tab,
  Tabs,
  Skeleton,
  Divider,
  Stack,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../theme";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import apiService from "../../../../../service/Api";

interface CrewMember {
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  average_rating: number;
  review_count: number;
  roles?: string[];
}

interface CrewPerformanceSummaryResponse {
  month: string;
  scope: string;
  rating_threshold: number;
  min_reviews_threshold: number;
  top_performers: CrewMember[];
  needs_attention: CrewMember[];
}

// ─── Star renderer ────────────────────────────────────────────────────────────

const StarRating = ({ value, color }: { value: number; color: string }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (value >= i) {
      stars.push(<StarIcon key={i} sx={{ fontSize: 16, color }} />);
    } else if (value >= i - 0.5) {
      stars.push(<StarHalfIcon key={i} sx={{ fontSize: 16, color }} />);
    } else {
      stars.push(
        <StarBorderIcon key={i} sx={{ fontSize: 16, color: "#ccc" }} />,
      );
    }
  }
  return <Box sx={{ display: "flex", alignItems: "center" }}>{stars}</Box>;
};

// ─── Medal colours for top 3 ──────────────────────────────────────────────────

const medalColour = (index: number) =>
  index === 0
    ? "#FFB300"
    : index === 1
      ? "#90A4AE"
      : index === 2
        ? "#A1674A"
        : null;

// ─── Single row ───────────────────────────────────────────────────────────────

const CrewRow = ({
  member,
  index,
  mode,
}: {
  member: CrewMember;
  index: number;
  mode: "top" | "low";
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const medal = mode === "top" ? medalColour(index) : null;
  const ratingColor =
    mode === "top"
      ? member.average_rating >= 4.5
        ? "#2e7d32"
        : "#f57c00"
      : member.average_rating <= 2
        ? "#c62828"
        : "#e65100";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        transition: "background 0.15s",
        "&:hover": { backgroundColor: colors.primary[800] },
      }}
    >
      {/* Rank / medal */}
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: medal
            ? medal
            : mode === "low"
              ? "#fdecea"
              : "#eeeeee",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: medal ? "#fff" : mode === "low" ? "#c62828" : "#616161",
          }}
        >
          {index + 1}
        </Typography>
      </Box>

      {/* Avatar */}
      <Avatar
        sx={{
          width: 40,
          height: 40,
          border: medal ? `2px solid ${medal}` : "2px solid #e0e0e0",
          flexShrink: 0,
        }}
      >
        {member.first_name.charAt(0)}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ color: "text.primary" }}
        >
          {member.first_name} {member.last_name}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.3}>
          {member.roles?.map((r) => (
            <Chip
              key={r}
              label={r}
              size="small"
              sx={{
                fontSize: 10,
                height: 18,
                backgroundColor: "action.hover",
                color: "#555",
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Rating */}
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 800,
            color: ratingColor,
            lineHeight: 1,
          }}
        >
          {member.average_rating.toFixed(1)}
        </Typography>
        <StarRating value={member.average_rating} color={ratingColor} />
        <Typography sx={{ fontSize: 10, color: "text.secondary", mt: 0.3 }}>
          {member.review_count} review{member.review_count !== 1 ? "s" : ""}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingRows = () => (
  <>
    {[1, 2, 3].map((i) => (
      <Box
        key={i}
        sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5, px: 2 }}
      >
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
        </Box>
        <Box>
          <Skeleton width={36} height={24} />
          <Skeleton width={80} height={14} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
    ))}
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CrewRatings: React.FC = () => {
  const [tab, setTab] = useState<0 | 1>(0);
  const [topCrew, setTopCrew] = useState<CrewMember[]>([]);
  const [lowCrew, setLowCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerformanceSummary = async () => {
      try {
        setLoading(true);

        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

        const res = await apiService.get<CrewPerformanceSummaryResponse>(
          `/api/management/dashboard/crew-performance-summary/?month=${month}`,
        );

        setTopCrew(res.data?.top_performers ?? []);
        setLowCrew(res.data?.needs_attention ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch crew performance summary.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceSummary();
  }, []);

  const isTop = tab === 0;
  const crew = isTop ? topCrew : lowCrew;

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 3,
        overflow: "hidden",
        maxWidth: 520,
        mt: 2,
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: isTop ? "#fff8e1" : "#fdecea",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.3s",
            }}
          >
            {isTop ? (
              <EmojiEventsIcon sx={{ color: "#FFB300", fontSize: 22 }} />
            ) : (
              <WarningAmberIcon sx={{ color: "#e53935", fontSize: 22 }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              Crew Ratings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isTop ? "Top performers this month" : "Crew needing attention"}
            </Typography>
          </Box>
        </Box>

        <Chip
          size="small"
          label="This Month"
          sx={{
            fontSize: 10,
            height: 20,
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 2,
          mt: 1.5,
          "& .MuiTab-root": {
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            minHeight: 36,
            py: 0,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: tab === 0 ? "#FFB300" : "#e53935",
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <EmojiEventsIcon sx={{ fontSize: 14 }} />
              Top Rated
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              Low Ratings
            </Box>
          }
        />
      </Tabs>

      <Divider />

      {/* Body */}
      <CardContent sx={{ p: 1, pb: "8px !important" }}>
        {loading ? (
          <LoadingRows />
        ) : error ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : crew.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              No crew in this category yet.
            </Typography>
          </Box>
        ) : (
          crew.map((member, i) => (
            <React.Fragment key={member.crew_member_id}>
              <CrewRow member={member} index={i} mode={isTop ? "top" : "low"} />
              {i < crew.length - 1 && <Divider sx={{ mx: 2, opacity: 0.4 }} />}
            </React.Fragment>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default CrewRatings;
